import React from "react";
import PropTypes from "prop-types";
import { Outlet, useNavigate } from "react-router-dom";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Typography, Button, Container } from "@mui/material";
import TopBackBar from "./TopBackBar";
import { getAppTheme, useAuth } from "../../services/authContext";
import LockIcon from '@mui/icons-material/Lock';

// A simple component to show when access is denied
const PermissionDenied = () => {
    const navigate = useNavigate();
    return (
        <Container>
            <Box
                sx={{
                    textAlign: 'center',
                    py: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <LockIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                    Permission Denied
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    You do not have the necessary permissions to view this page.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/')}>
                    Go to Homepage
                </Button>
            </Box>
        </Container>
    );
};


const LayoutWithBackButton = ({ title = "Make Loan Eazy MLE", to = "/", showBackButton = true, permission = "all" }) => {
    const { loginStatus } = useAuth();
    const theme = getAppTheme();

    // --- Permission Check Logic ---
    const checkPermission = () => {
        const isLoggedIn = !!loginStatus.token;
        const isAdmin = loginStatus?.user?.staff_type === 'admin';

        if (permission === "all") {
            return true;
        }
        if (permission === "loggedIn" && isLoggedIn) {
            return true;
        }
        if (permission === "admin" && isLoggedIn && isAdmin) {
            return true;
        }
        return false;
    };

    const hasPermission = checkPermission();

    // This component will render either the Outlet (page content) or the PermissionDenied message
    const renderContent = () => {
        if (hasPermission) {
            return <Outlet />;
        }
        return <PermissionDenied />;
    };

    return (
        <ThemeProvider theme={theme}>
            {showBackButton ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        width: "100%",
                        flexDirection: "column",
                        paddingTop: "100px",
                        paddingBottom: "100px",
                        marginInline: "auto",
                    }}
                >
                    <TopBackBar title={title} to={to} />
                    <Box
                        sx={{
                            flex: 1,
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Box
                            style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                            }}
                            backgroundColor="transparent"
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: "auto",
                                    WebkitOverflowScrolling: "touch",
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Box sx={{ position: "relative" }}>
                                    {renderContent()}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : (
                renderContent()
            )}
        </ThemeProvider>
    );
};

LayoutWithBackButton.propTypes = {
    title: PropTypes.string,
    to: PropTypes.string,
    showBackButton: PropTypes.bool,
    // Updated prop type for permission
    permission: PropTypes.oneOf(['all', 'loggedIn', 'admin']),
};

export default LayoutWithBackButton;
